import Vex from 'vexflow';

const { Factory } = Vex.Flow;

const HIGH_BRASS = ['trumpet', 'frenchHorn'];
const NOTE_PATTERN = /^([A-G])([b#]?)(\d)$/;
const BASS_SHIFT = { C: 'A', D: 'B', E: 'C', F: 'D', G: 'E', A: 'F', B: 'G' };

// The staff is drawn with treble-clef note positions, so bass-clef
// instruments get their note names relabelled to land on the right lines.
// (Same conversion the old ScaleNotes used.)
function toBassPosition(note) {
  const match = NOTE_PATTERN.exec(note);
  if (!match) return note;
  const [, base, accidental, octave] = match;
  let newOctave = parseInt(octave, 10) + 1;
  if (base !== 'C' && base !== 'D') newOctave += 1;
  return `${BASS_SHIFT[base]}${accidental}${newOctave}`;
}

// Accidentals come from the key signature, so the notes themselves are drawn
// without them.
function stripAccidental(note) {
  const match = NOTE_PATTERN.exec(note);
  return match ? `${match[1]}${match[3]}` : note;
}

/**
 * Draws one long staff (clef, key signature, N/4 time signature, tempo mark)
 * into the element with the given id and reports where each note landed so the
 * playhead bar can sit on it.
 *
 * Returns { noteXs, pxWidth, pxHeight } in screen pixels, or null if nothing
 * was drawn.
 */
export function drawScale({
  elementId,
  notes,
  instrument,
  bpm,
  boxWidth,
  color = '#2a2118',
  wrongColor = '#c8321e',
  rightColor = '#2e8b57',
  noteStates = [],
}) {
  const host = document.getElementById(elementId);
  if (!host) return null;
  host.innerHTML = '';
  if (!notes.length || boxWidth < 200) return null;

  const isHigh = HIGH_BRASS.includes(instrument);
  const clef = isHigh ? 'treble' : 'bass';
  const shown = notes.map((note) => stripAccidental(isHigh ? note : toBassPosition(note)));
  const tonicMatch = NOTE_PATTERN.exec(notes[0]);
  const keySignature = tonicMatch ? `${tonicMatch[1]}${tonicMatch[2]}` : 'C';
  const timeSignature = `${notes.length}/4`;

  // Draw in VexFlow's own units, then let the SVG scale up to the pixel width.
  const scale = Math.min(1.6, Math.max(0.9, boxWidth / 850));
  const width = Math.floor(boxWidth / scale);
  const height = 170;

  const vf = new Factory({ renderer: { elementId, width, height } });
  const context = vf.getContext();
  context.setFillStyle(color);
  context.setStrokeStyle(color);

  const score = vf.EasyScore();
  const system = vf.System({
    x: 10,
    y: 55,
    width: width - 20,
    formatOptions: { autoBeam: false },
  });

  const noteObjects = score.notes(shown.map((note) => `${note}/q`).join(', '), {
    stem: 'auto',
  });
  noteObjects.forEach((note, index) => {
    const state = noteStates[index];
    const noteColor = state === 'wrong' ? wrongColor : state === 'right' ? rightColor : color;
    note.setStyle({ fillStyle: noteColor, strokeStyle: noteColor });
    note.setLedgerLineStyle({ fillStyle: noteColor, strokeStyle: noteColor });
  });

  const voice = score.voice(noteObjects, { time: timeSignature });
  voice.setStrict(false);
  voice.setMode(Vex.Flow.Voice.Mode.SOFT);

  const stave = system.addStave({ voices: [voice] });
  stave.setStyle({ fillStyle: color, strokeStyle: color });
  stave.addClef(clef).addKeySignature(keySignature).addTimeSignature(timeSignature);
  stave.setTempo({ duration: 'q', bpm }, -27);
  stave.setMeasure(1);

  vf.draw();

  const noteXs = noteObjects.map((note) => {
    const begin = note.getNoteHeadBeginX();
    const end = note.getNoteHeadEndX();
    return ((begin + end) / 2) * scale;
  });

  const svg = host.querySelector('svg');
  if (svg) {
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    svg.setAttribute('width', String(width * scale));
    svg.setAttribute('height', String(height * scale));
    // Inline size as well as attributes: VexFlow leaves its own width on the
    // element, which kept the staff at its unscaled size while the note
    // positions were scaled, so the bar ended up far to the right of the notes.
    svg.style.display = 'block';
    svg.style.width = `${width * scale}px`;
    svg.style.height = `${height * scale}px`;
    svg.style.maxWidth = 'none';
  }

  return { noteXs, pxWidth: width * scale, pxHeight: height * scale };
}